import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { getChatbotResponse } from '../../../services/openaiService';

const ChatSimulation = () => {
  const [messages, setMessages] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isExpanded]);

  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState(null);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [welcomeMessageVisible, setWelcomeMessageVisible] = useState(false);
  const messagesEndRef = useRef(null);
  const chatRef = useRef(null);
  const conversationHistory = useRef([]);

  const scrollToChatIfOffscreen = () => {
    setTimeout(() => {
      const el = document.getElementById('chat-simulation-container');
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 100 || rect.top > window.innerHeight - 100) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 500);
  };

  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Intersection Observer для приветственного сообщения
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !showWelcomeMessage && messages.length === 0) {
            setShowWelcomeMessage(true);
            setTimeout(() => {
              setWelcomeMessageVisible(true);
            }, 300);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (chatRef.current) {
      observer.observe(chatRef.current);
    }

    return () => {
      if (chatRef.current) {
        observer.unobserve(chatRef.current);
      }
    };
  }, [showWelcomeMessage, messages.length]);

  const handleSendMessage = async () => {
    if (!inputMessage?.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputMessage,
      avatar: "/images/rocket_gen_img_1fb6cf439-1763299224286.webp",
      avatarAlt: "Professional woman with long brown hair wearing white blouse smiling at camera in bright office setting"
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);
    setError(null);

    conversationHistory?.current?.push({
      role: 'user',
      content: inputMessage
    });

    try {
      const response = await getChatbotResponse(inputMessage, conversationHistory?.current);
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: response
      };

      conversationHistory?.current?.push({
        role: 'assistant',
        content: response
      });

      setMessages(prev => [...prev, botMessage]);
      scrollToChatIfOffscreen();
    } catch (err) {
      setError(err?.message || 'Произошла ошибка при получении ответа');
      console.error('Chat error:', err);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-[#0F172A]">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-headline font-bold text-white mb-4">
              Увидьте AI в действии
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Наш интеллектуальный ассистент обрабатывает запросы в режиме реального времени, обеспечивая мгновенные и точные ответы
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div 
              id="chat-simulation-container" 
              className={`bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-[#00D4FF]/20 flex flex-col transition-all duration-300 ${
                isExpanded ? 'fixed inset-0 z-[100] h-[100dvh] w-full rounded-none border-0 shadow-none' : 'relative rounded-3xl p-6 md:p-8 border shadow-2xl'
              }`}
            >
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-full flex items-center justify-center">
                    <Icon name="Bot" size={20} color="#FFFFFF" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Jetu AI Assistant</h3>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-xs text-gray-400">Online</span>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <button 
                    className="md:hidden p-2 text-white/80 hover:text-white rounded-full transition-colors"
                    onClick={() => setIsExpanded(false)}
                  >
                    <Icon name="X" size={24} color="currentColor" />
                  </button>
                )}
              </div>

              <div ref={chatRef} className={`space-y-4 overflow-y-auto pr-2 custom-scrollbar ${isExpanded ? 'flex-1 px-4' : 'h-[350px] md:h-96'}`}>
                {showWelcomeMessage && welcomeMessageVisible && messages.length === 0 && (
                  <div className="flex justify-start animate-fade-in mb-4">
                    <div className="flex items-start space-x-2 max-w-[80%]">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Bot" size={16} color="#FFFFFF" />
                      </div>
                      <div className="bg-white/10 rounded-2xl px-4 py-3 animate-slide-up">
                        <p className="text-sm leading-relaxed text-gray-200 whitespace-pre-wrap">
                          Привет! 👋 Я AI-ассистент Jetu AI. Готов помочь вам с вопросами о разработке сайтов, мобильных приложений и автоматизации бизнеса. Чем могу помочь?
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {messages?.map((message) => (
                  <div
                    key={message?.id}
                    className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}
                  >
                    <div className={`flex items-start space-x-2 max-w-[80%] ${message?.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      {message?.type === 'user' ? (
                        <Image
                          src={message?.avatar}
                          alt={message?.avatarAlt}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon name="Bot" size={16} color="#FFFFFF" />
                        </div>
                      )}
                      <div
                        className={`rounded-2xl px-4 py-3 ${
                          message?.type === 'user' ?'bg-gradient-to-r from-[#00D4FF] to-[#9D4EDD] text-white' :'bg-white/10 text-gray-200'
                        }`}
                      >
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">{message?.text}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start animate-slide-up">
                    <div className="flex items-start space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-full flex items-center justify-center">
                        <Icon name="Bot" size={16} color="#FFFFFF" />
                      </div>
                      <div className="bg-white/10 rounded-2xl px-4 py-3">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex justify-center">
                    <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2 text-red-400 text-sm">
                      {error}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex items-center space-x-2 bg-white/5 rounded-xl px-4 py-3">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    onFocus={() => { if (window.innerWidth < 768) setIsExpanded(true); }}
                    placeholder="Введите сообщение..."
                    className="flex-1 bg-transparent text-white placeholder-gray-400 outline-none text-base md:text-sm"
                    disabled={isTyping}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage?.trim() || isTyping}
                    className={`transition-opacity ${!inputMessage?.trim() || isTyping ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                  >
                    <Icon name="Send" size={20} color="#00D4FF" />
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="Zap" size={24} color="#FFFFFF" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Мгновенные ответы</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Обработка запросов менее чем за 2 секунды с точностью понимания контекста 95%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="Globe" size={24} color="#FFFFFF" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Многоканальная интеграция</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Единая платформа для WhatsApp, Instagram, Telegram и веб-чата
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.2)]">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#00D4FF] to-[#9D4EDD] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon name="Brain" size={24} color="#FFFFFF" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Самообучающийся AI</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Постоянное улучшение на основе взаимодействий и обратной связи клиентов
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatSimulation;